import React, { useState } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { ImageItem, MediaItem } from '../utils/imageUtils';

interface ImageGalleryProps {
  images: MediaItem[];
  maxThumbnails?: number;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images,
  maxThumbnails = 6 
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!images || images.length === 0) {
    return null;
  }

  const handleImageClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleClose = () => {
    setSelectedIndex(null);
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < images.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleDownload = (image: MediaItem) => {
    // For PDFs, use the documentUrl if available, otherwise use the regular URL
    const url = (image.type === 'pdf' && image.documentUrl) ? image.documentUrl : image.url;
    const filename = image.filename || image.title;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const displayImages = images.slice(0, maxThumbnails);
  const remainingCount = images.length - maxThumbnails;

  return (
    <>
      {/* Thumbnail Grid */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 1.5, 
            color: theme.palette.text.secondary,
            fontWeight: 500 
          }}
        >
          📎 Found {images.length} file{images.length > 1 ? 's' : ''}:
        </Typography>
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)', 
              md: 'repeat(4, 1fr)',
            },
            gap: 1.5,
          }}
        >
          {displayImages.map((image, index) => (
            <Box key={index}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={() => handleImageClick(index)}
              >
                <CardMedia
                  component="img"
                  height={isMobile ? 80 : 120}
                  image={image.url}
                  alt={image.title}
                  sx={{
                    objectFit: 'cover',
                    backgroundColor: '#f5f5f5',
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIxIDE5VjVDMjEgMy45IDIwLjEgMyAxOSAzSDVDMy45IDMgMyAzLjkgMyA1VjE5QzMgMjAuMSAzLjkgMjEgNSAyMUgxOUMyMC4xIDIxIDIxIDIwLjEgMjEgMTlaIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41IiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CjxwYXRoIGQ9Ik0yMSAxNUwxNiAxMEw1IDIxIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPgo=';
                  }}
                />
                
                {/* Overlay with zoom icon */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  <ZoomInIcon 
                    sx={{ 
                      color: 'white', 
                      fontSize: 24,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '.MuiCard-root:hover &': {
                        opacity: 1,
                      },
                    }} 
                  />
                </Box>

                {/* Source type badge */}
                {image.source_type && (
                  <Chip
                    label={image.source_type.toUpperCase()}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      fontSize: '0.6rem',
                      height: 18,
                      backgroundColor: 'rgba(0, 172, 221, 0.9)',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                )}
                
                {/* PDF badge */}
                {image.type === 'pdf' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      right: 4,
                      backgroundColor: 'rgba(211, 47, 47, 0.95)',
                      color: 'white',
                      borderRadius: 1,
                      px: 0.75,
                      py: 0.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    <PdfIcon sx={{ fontSize: 13 }} />
                    PDF
                  </Box>
                )}
              </Card>
            </Box>
          ))}

          {/* Show remaining count if there are more images */}
          {remainingCount > 0 && (
            <Box>
              <Card
                sx={{
                  height: isMobile ? 80 : 120,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 172, 221, 0.1)',
                  border: '2px dashed rgba(0, 172, 221, 0.3)',
                  cursor: 'pointer',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 172, 221, 0.2)',
                  },
                }}
                onClick={() => handleImageClick(maxThumbnails)}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#00ACDD',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  +{remainingCount}<br />
                  more
                </Typography>
              </Card>
            </Box>
          )}
        </Box>
      </Box>

      {/* Full Screen Modal */}
      <Dialog
        open={selectedIndex !== null}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            margin: 0,
            maxHeight: '100vh',
            maxWidth: '100vw',
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedIndex !== null && (
            <>
              {/* Close button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Download button */}
              <IconButton
                onClick={() => handleDownload(images[selectedIndex])}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 70,
                  zIndex: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>

              {/* Navigation buttons */}
              {images.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevious}
                    disabled={selectedIndex === 0}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>

                  <IconButton
                    onClick={handleNext}
                    disabled={selectedIndex === images.length - 1}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}

              {/* Main image */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 'calc(100vh - 200px)', // Full height minus space for controls and info
                  maxHeight: '80vh',
                  p: { xs: 2, sm: 3, md: 4 },
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {images[selectedIndex].type === 'pdf' && images[selectedIndex].documentUrl ? (
                    // Show PDF iframe
                    <iframe
                      src={images[selectedIndex].documentUrl}
                      title={images[selectedIndex].title}
                      style={{
                        width: '90vw',
                        height: '80vh',
                        maxWidth: '1400px',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                      }}
                    />
                  ) : (
                    // Show image
                    <img
                      src={images[selectedIndex].url}
                      alt={images[selectedIndex].title}
                      style={{
                        maxWidth: '90vw',
                        maxHeight: '70vh',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        display: 'block',
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjEgMTlWNUMyMSAzLjkgMjAuMSAzIDE5IDNINUMzLjkgMyAzIDMuOSAzIDVWMTlDMyAyMC4xIDMuOSAyMSA1IDIxSDE5QzIwLjEgMjEgMjEgMjAuMSAyMSAxOVoiIHN0cm9rZT0iI2NjYyIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48Y2lyY2xlIGN4PSI4LjUiIGN5PSI4LjUiIHI9IjEuNSIgc3Ryb2tlPSIjY2NjIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjxwYXRoIGQ9Ik0yMSAxNUwxNiAxMEw1IDIxIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+';
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Image info */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                  p: 3,
                  pt: 6,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {images[selectedIndex].title}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {images[selectedIndex].source_type && (
                    <Chip
                      label={images[selectedIndex].source_type?.toUpperCase() || 'UNKNOWN'}
                      size="small"
                      sx={{
                        backgroundColor: '#00ACDD',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  
                  {images[selectedIndex].confidence_score && (
                    <Chip
                      label={`${Math.round(images[selectedIndex].confidence_score! * 100)}% match`}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(76, 175, 80, 0.9)',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      ml: 'auto',
                    }}
                  >
                    {selectedIndex + 1} of {images.length}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageGallery;
